import React, {useEffect} from 'react'
import {Checkbox, Typography} from 'antd'
import classNames from 'classnames'
import {useLocation} from 'react-router-dom'
import {observer} from 'mobx-react'
import {useTranslation} from 'react-i18next'

import {getRouterStore} from '@/router/utils'
import {useApplicationStepsStore} from '@/stores/onboarding/applicationStepsUtils'
import {ApplicationStep, ApplicationSubStep} from '@/stores/onboarding/models/onboarding'

import {ApplicationStepsProps} from './props'
import styles from './ApplicationSteps.scss'

const ApplicationSteps: React.FC<ApplicationStepsProps> = () => {
    const location = useLocation()
    const currentPath = location.pathname
    const stepsStore = useApplicationStepsStore()

    const {t} = useTranslation()

    useEffect(() => {
        stepsStore.updateCurrentStep(currentPath)
    }, [currentPath, stepsStore])

    const getCheckboxState = (step: ApplicationStep | ApplicationSubStep): boolean => {
        return step.completed || false
    }

    const handleStepClick = (step: ApplicationStep | ApplicationSubStep) => {
        if (!stepsStore.isStepClickable(step) || !step.route) return
        getRouterStore().push(step.route)
    }

    return (
        <div className={styles.stepsContainer}>
            <Typography.Text type={'secondary'} className={styles.applicationTitle}>
                {t('ApplicationProgress.steps.application.title')}
            </Typography.Text>

            <div className={styles.stepsList}>
                {stepsStore.steps.map((step) => {
                    const stepElement = (
                        <div
                            className={classNames(
                                styles.stepItem,
                                {
                                    [styles.activeStep]: step.active,
                                    [styles.disabledStep]: step.disabled,
                                    [styles.clickableStep]: stepsStore.isStepClickable(step)
                                },
                                stepsStore.isNextAvailableStep(step) ? 'nextAvailableStep' : ''
                            )}
                            onClick={() => handleStepClick(step)}
                            style={{
                                cursor: stepsStore.isStepClickable(step) ? 'pointer' : 'default'
                            }}
                        >
                            <Checkbox
                                className={styles.stepCheckbox}
                                checked={getCheckboxState(step)}
                                disabled={true}
                            />
                            <span className={styles.stepTitle}>{step.title}</span>
                        </div>
                    )

                    return (
                        <div key={step.key}>
                            {stepElement}

                            {step.subSteps && step.expanded && (
                                <div className={styles.subSteps}>
                                    {step.subSteps.map((subStep) => {
                                        const subStepElement = (
                                            <div
                                                className={classNames(
                                                    styles.subStepItem,
                                                    {
                                                        [styles.activeStep]:
                                                            subStep.active ||
                                                            subStep.key ===
                                                                stepsStore.currentStepKey,
                                                        [styles.disabledStep]: subStep.disabled,
                                                        [styles.clickableStep]:
                                                            stepsStore.isStepClickable(subStep)
                                                    },
                                                    stepsStore.isNextAvailableStep(subStep)
                                                        ? 'nextAvailableStep'
                                                        : ''
                                                )}
                                                onClick={() => handleStepClick(subStep)}
                                                style={{
                                                    cursor: stepsStore.isStepClickable(subStep)
                                                        ? 'pointer'
                                                        : 'default'
                                                }}
                                            >
                                                <Checkbox
                                                    className={styles.stepCheckbox}
                                                    checked={getCheckboxState(subStep)}
                                                    disabled={true}
                                                />
                                                <span className={styles.stepTitle}>
                                                    {subStep.title}
                                                </span>
                                            </div>
                                        )

                                        return <div key={subStep.key}>{subStepElement}</div>
                                    })}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default observer(ApplicationSteps)
